import { motion } from 'framer-motion'
import AnimatedHeading from '@/Components/UI/AnimatedHeading'
import { LocationOutlinedIcon } from '@/Components/UI/Icons'
import { type LocationSelectionData } from '@/Components/PreferredLocation/LocationSection'
import { type PreviousEventRequest } from '@/Components/RepeatEvent/PreviousEventCard'
import { isCurrency, CURRENCY_SYMBOLS } from '@/Constants/currencies'
import SummaryLocationMap from './SummaryLocationMap'

interface SummaryRow {
  label: string
  value: string | undefined | null
}

interface SummaryCardProps {
  delay: number
  items: SummaryRow[]
}

const SummaryCard = ({ delay, items }: SummaryCardProps) => {
  const visibleItems = items.filter((item) => !!item.value)
  if (!visibleItems.length) return null
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: 'easeOut' }}
      className='bg-white border border-[#d9d9d9] rounded p-4'>
      <div className='flex flex-col gap-2'>
        {visibleItems.map((item) => (
          <div key={item.label} className='flex flex-col sm:flex-row sm:gap-4'>
            <p className='font-[Montserrat]'>
              <span className='font-semibold text-[#1f1f1f]'>{item.label}:</span>{' '}
              <span className='text-[#595959]'>{item.value}</span>
            </p>
          </div>
        ))}
      </div>
    </motion.div>
  )
}

const VenueCard = ({ venue }: { venue: PreviousEventRequest }) => (
  <div className='flex items-center gap-2 border border-[#d9d9d9] rounded-[8px] p-2'>
    <div
      className='w-[66px] h-[60px] rounded-[8px] shrink-0 bg-[#d9d9d9] bg-cover bg-center'
      style={venue.imageUrl ? { backgroundImage: `url("${venue.imageUrl}")` } : undefined}
    />
    <div className='flex-1 min-w-0 px-2'>
      <p className='font-[Montserrat] font-semibold text-base text-black truncate'>
        {venue.spaceName}
      </p>
      <div className='flex items-center gap-1'>
        <LocationOutlinedIcon className='shrink-0 text-[#8c8c8c]' />
        <span className='font-[Montserrat] text-sm text-[#595959] truncate'>
          {venue.location ?? venue.venueName}
        </span>
      </div>
    </div>
  </div>
)

interface EventSummaryScreenProps {
  eventName: string
  eventType?: string
  guests?: number | ''
  dateRange: string
  time: string
  flexibility?: string
  budget?: number | ''
  currency?: string | null
  styles?: string[]
  layout?: string
  facilities?: string[]
  cateringNeeds?: string
  location: LocationSelectionData | null
  additionalNotes?: string
  requestedVenues?: PreviousEventRequest[]
}

const UK_POSTCODE = / [A-Z]{1,2}\d[A-Z\d]?( \d[A-Z]{2})?$/i

const formatArea = (address: string): string => {
  const [town = '', country = ''] = address.split(', ').slice(-2)
  return [town.replace(UK_POSTCODE, ''), country].filter(Boolean).join(', ')
}

const formatLocation = (location: LocationSelectionData): string => {
  if (location.mode === 'radius') {
    return `${location.location} · ${location.radiusKm}km radius`
  }
  return formatArea(location.location)
}

const EventSummaryScreen = ({
  eventName,
  eventType,
  guests,
  dateRange,
  time,
  flexibility,
  budget,
  currency,
  styles,
  layout,
  facilities,
  cateringNeeds,
  location,
  additionalNotes,
  requestedVenues,
}: EventSummaryScreenProps) => (
  <div className='flex flex-col gap-3 md:gap-4'>
    <AnimatedHeading size='large'>Confirm your event details:</AnimatedHeading>

    <motion.p
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.25, ease: 'easeOut' }}
      className='font-[Montserrat] !text-base text-[#595959]'>
      Check it&apos;s all correct before requesting your quotes
    </motion.p>

    <SummaryCard
      delay={0.3}
      items={[
        { label: 'Event name', value: eventName },
        { label: 'Occasion', value: eventType },
      ]}
    />

    <SummaryCard
      delay={0.4}
      items={[
        {
          label: 'Attendees',
          value: guests ? `${guests}${layout ? `, ${layout}` : ''}` : null,
        },
        { label: 'Venue styles', value: styles?.length ? styles.join(', ') : null },
      ]}
    />

    <SummaryCard
      delay={0.5}
      items={[
        { label: 'Event date', value: dateRange },
        { label: 'Timings', value: time },
        { label: 'Flexibility', value: flexibility },
      ]}
    />

    <SummaryCard
      delay={0.6}
      items={[
        {
          label: 'Budget',
          value: budget
            ? `${isCurrency(currency) ? CURRENCY_SYMBOLS[currency] : '£'}${Number(
                budget
              ).toLocaleString()}`
            : null,
        },
      ]}
    />

    <SummaryCard
      delay={0.7}
      items={[
        { label: 'Desired facilities', value: facilities?.length ? facilities.join(', ') : null },
      ]}
    />

    <SummaryCard
      delay={0.8}
      items={[
        { label: 'Additional notes', value: additionalNotes },
        { label: 'Catering needs', value: cateringNeeds },
      ]}
    />

    {location && (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.9, ease: 'easeOut' }}
        className='bg-white border border-[#d9d9d9] rounded p-4'>
        <div className='flex flex-col gap-2'>
          <p className='font-[Montserrat]'>
            <span className='font-semibold text-[#1f1f1f]'>Location:</span>{' '}
            <span className='text-[#595959]'>{formatLocation(location)}</span>
          </p>
          <div className='mt-1 rounded-[4px] overflow-hidden'>
            <SummaryLocationMap location={location} />
          </div>
        </div>
      </motion.div>
    )}

    {requestedVenues && requestedVenues.length > 0 && (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0, ease: 'easeOut' }}
        className='bg-white border border-[#d9d9d9] rounded p-4'>
        <div className='flex flex-col gap-2'>
          <p className='font-[Montserrat] font-semibold text-[#1f1f1f]'>
            Requested venues ({requestedVenues.length}):
          </p>
          <div className='flex flex-col gap-2'>
            {requestedVenues.map((venue, i) => (
              <VenueCard key={i} venue={venue} />
            ))}
          </div>
        </div>
      </motion.div>
    )}
  </div>
)

export default EventSummaryScreen
